import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'next/router';
import { Button, Typography, withStyles, IconButton } from '@material-ui/core';
import Carousel from 'nuka-carousel';
import AnimateHeight from 'react-animate-height';
import { confirmAlert } from 'react-confirm-alert';
import { isIOS, isChrome } from 'react-device-detect';
import { AnimateSharedLayout, AnimatePresence, motion } from 'framer-motion';
import {
  TrashIcon,
  MapIcon,
  Share,
  SettingsIcon,
  ThreeDimensionIcon,
  StreetViewIcon,
} from '../../utility/Icons';
import ButtonWithSnackBar from '../../utility/SnackBarButton';
import ToriiLoader from '../../utility/ToriiLoader';
import Details from './Details';
import Tour from './Tour';
import Estimate from './Estimate';
import Similar from './Similar';
import Photos from './Photos';
import Schools from './Schools';

import { isMobile, isTablet } from '../../../lib/getDeviceSize';
import { blockListing } from '../../../actions/blocked';
import {
  fetchNeighborhoodScore,
  fetchOpenHousesByListingId,
  fetchSchools,
  toggleListingModal,
} from '../../../actions/listing';
import {
  addressString,
  checkStatusLive,
  formatBathrooms,
  normalizePrice,
  formatUrlAddress,
  extractValidUrl,
  checkStatusSold,
  allowListingActions,
} from '../../../lib/helpers';
import FavoriteButton from '../../utility/FavoriteButton';
import * as types from '../../../actions/actionTypes';
import track from '../../../lib/track';
import styles from './styles';
import EventEmitter from '../../../lib/eventEmitter';
import { STREETVIEW_MAX_DISTANCE } from './Map';
import Neighborhood from './Neighborhood';
import {
  ClearSignupCallback,
  OpenSignupDialog,
} from '../../../actions/actionTypes';
import { X } from 'phosphor-react';
import BackButton from '../../utility/BackButton';
import { updateProfile } from '../../../actions/auth';
import { get } from 'lodash';

const HEADER_HEIGHT = 70;
export const DETAILS_HEADER_HEIGHT = 75;
export const TABS_HEADER_HEIGHT = 50;
export const ENABLE_3D_VIEW = true;
export const ENABLE_STREET_VIEW = true;

declare global {
  interface Window {
    google: any;
  }
  namespace JSX {
    interface IntrinsicElements {
      'realreports-widget': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > & {
        token: string;
        address: string;
        latitude: string;
        longitude: string;
        'leads-email': string;
        theme: string;
        fields: string;
        'mls-listing-id': string;
        'mls-ouid': string;
      };
    }
  }
}

if (typeof window !== 'undefined') {
  window.google = window.google || {};
}
export interface Props {
  classes: any;
  dispatch: any;
  favorites: any;
  listing: any;
  router: any;
  token: string;
  user: any;
  showInModal?: boolean;
}
export interface State {
  currentImageIndex: number;
  currentIndex: number;
  drawerOpen: boolean;
  height: number;
  loading: boolean;
  mapMarkers: any[];
  routes: any;
  show3DView: boolean;
  show3DViewButton: boolean;
  showEditButton: boolean;
  showImage: boolean;
  showMap: boolean;
  showMapButton: boolean;
  showStreetView: boolean;
  showStreetViewButton: boolean;
  streetViewSupported: boolean;
  width: number;
}

export class Listing extends React.Component<Props, State> {
  headerRef: any;
  tabBarRef: any;
  supportsCopy: any;
  supportsNativeShare: any;
  _tabItems: any = [];
  googleCheckTries: number = 0;
  constructor(props: any) {
    super(props);
    this.headerRef = React.createRef();
    this.tabBarRef = React.createRef();
    this.state = {
      currentImageIndex: 0,
      currentIndex: 0,
      drawerOpen: true,
      height: 600,
      loading: true,
      mapMarkers: [],
      routes: [
        { key: 'photos', title: 'Photos' },
        { key: 'details', title: 'Details' },
        { key: 'insights', title: 'Insights' },
        { key: 'neighborhood', title: 'Neighborhood' },
        { key: 'schools', title: 'Schools' },
        { key: 'tour', title: 'Tour' },
        { key: 'estimate', title: 'Estimate' },
        { key: 'similar', title: 'Similar' },
      ],
      show3DView: false,
      show3DViewButton: false,
      showEditButton: true,
      showImage: false,
      showMap: false,
      showMapButton: true,
      showStreetView: false,
      showStreetViewButton: true,
      streetViewSupported: false,
      width: 0,
    };
  }

  componentDidMount() {
    this.supportsCopy =
      typeof navigator !== 'undefined' &&
      navigator.clipboard &&
      navigator.clipboard.writeText;
    this.supportsNativeShare =
      typeof navigator !== 'undefined' && navigator.share;
    setTimeout(() => {
      if (isMobile()) {
        this.scrollToMyRef(false);
        // Check conditions for 3D-view and Street-view
        if (
          this.props.listing.virtualTourUrl &&
          extractValidUrl(this.props.listing.virtualTourUrl)
        ) {
          this.setState({
            show3DViewButton: true,
          });
        }
        this.handleGoogleStreeView();
      }
      const { view3d } = this.props.router.query;
      if (view3d) {
        this.view3dHelper();
      }
    }, 1000);
    if (isMobile()) {
      setTimeout(() => {
        this.setState({ drawerOpen: false });
      }, 3500);
    }
    if (typeof window !== 'undefined') {
      this.updateWindowDimensions();
    }
    window.addEventListener('resize', this.updateWindowDimensions);
    this.fetchSchoolsHelper(this.props.listing);
    track.listingView(this.props.listing);
    this.fetchNeighborhood(this.props.listing);
    this.fetchOpenHouses(this.props.listing);
  }

  view3dHelper = () => {
    const { listing } = this.props;
    if (
      listing.virtualTourUrl &&
      extractValidUrl(listing.virtualTourUrl) &&
      listing.virtualTourUrl.includes('matterport')
    ) {
      this.setState({
        show3DView: true,
        show3DViewButton: false,
      });
    }
  };

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  fetchSchoolsHelper = (listing: any) => {
    const values = {
      lat: listing.latitude,
      lon: listing.longitude,
    };
    this.props.dispatch(fetchSchools(values, 5, 20));
  };

  fetchOpenHouses = (listing: any) => {
    this.props.dispatch(fetchOpenHousesByListingId(listing.listingMlsId));
  };

  fetchNeighborhood = (listing: any) => {
    this.props.dispatch(
      fetchNeighborhoodScore(
        listing.listingId,
        listing.listingMlsId,
        listing.latitude,
        listing.longitude,
      ),
    );
  };

  handleGoogleStreeView = () => {
    const { listing } = this.props;
    const google = window.google;
    if (google && google.maps) {
      this.googleCheckTries = 0;
      // Check if this Lat/Lng supports street view
      const streetViewService = new google.maps.StreetViewService();
      const latLng = new google.maps.LatLng(
        listing.latitude,
        listing.longitude,
      );
      try {
        streetViewService.getPanoramaByLocation(
          latLng,
          STREETVIEW_MAX_DISTANCE,
          (streetViewPanoramaData: any, status: any) => {
            if (status === google.maps.StreetViewStatus.OK) {
              // Supports street view
              this.setState({ streetViewSupported: true });
            } else {
              // No street view available in this range, or some error occurred
              this.setState({ streetViewSupported: false });
            }
          },
        );
      } catch (err) {
        // There are no panoramas found that match the search criteria
        this.setState({ streetViewSupported: false });
      }
    } else {
      // Google is not found on window when the screen is mounted
      // So retry after 3 sec - max no. of tries 2
      if (this.googleCheckTries <= 2) {
        setTimeout(() => {
          this.googleCheckTries += 1;
          this.handleGoogleStreeView();
        }, 1500);
      }
    }
  };

  scrollToMyRef = (toHeader: boolean) => {
    if (toHeader) {
      if (this.headerRef && this.headerRef.current) {
        this.headerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    } else {
      if (!toHeader && this.tabBarRef && this.tabBarRef.current) {
        this.tabBarRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  };

  updateWindowDimensions = () => {
    this.setState({
      height:
        isIOS && isChrome
          ? document.documentElement.clientHeight
          : window.innerHeight,
      loading: false,
      width:
        isIOS && isChrome
          ? document.documentElement.clientWidth
          : window.innerWidth,
    });
  };

  handleOfferClick = () => {
    const { dispatch, listing, router } = this.props;
    if (allowListingActions(listing)) {
      track.buttonClick('START AN OFFER', router.asPath, 'Offer Dialog');
      dispatch({
        listing,
        type: types.OpenOfferDialog,
      });
    } else {
      dispatch({
        alertDialogHeading: 'Sorry, submit an offer is not available',
        alertDialogSubText: 'offer',
        type: types.OpenAlertDialog,
      });
    }
  };

  handleRequest = (listing: any) => {
    const { user, token } = this.props;
    if (isMobile() || isTablet()) {
      this.props.router.push({
        pathname: '/contact',
        query: token
          ? {
              email: user.email,
              location: listing.state,
              name: `${user.firstName} ${user.lastName}`,
              address: addressString(listing, true, false),
              message: `Hi, I have a question about ${addressString(
                listing,
                false,
                true,
              )}.`,
              listingMlsId: listing.listingMlsId,
            }
          : {},
      });
    } else {
      const query = token
        ? `?email=${user.email}&location=${listing.state}&name=${
            user.firstName
          } ${user.lastName}&address=${addressString(
            listing,
            true,
            false,
          )}&message=Hi, I have a question about ${addressString(
            listing,
            false,
            true,
          )}.&listingMlsId=${listing.listingMlsId}`
        : ``;
      const win: any = window.open(`/contact${query}`, '_blank');
      win.focus();
    }
  };

  handleTour = () => {
    this.handleTabChange(4);
  };

  _renderPrice = (listing: any) => (
    <Typography className={this.props.classes.priceStyle}>
      $
      {normalizePrice(
        listing.salePrice ? listing.salePrice : listing.listPrice,
      )}
    </Typography>
  );

  _renderDeatils = (listing: any) => (
    <Typography className={this.props.classes.detailsStyle}>
      {listing.noBedrooms} br | {formatBathrooms(listing)}ba |{' '}
      {listing.squareFeet ? normalizePrice(listing.squareFeet) : '--'} sqft
    </Typography>
  );

  _renderAddress = (listing: any) => (
    <h1 className={this.props.classes.addressStyle}>
      {addressString(listing, false, true)}
    </h1>
  );

  _renderStatus = (listing: any) => {
    const { classes } = this.props;
    const activeStatus = checkStatusLive(listing);
    return (
      <div className={classes.statusContainer}>
        <div
          className={classes.statusIndicator}
          style={{ backgroundColor: activeStatus ? '#195A50' : '#E2658C' }}
        />
        <Typography className={classes.statusText}>{listing.status}</Typography>
      </div>
    );
  };

  _renderIcons = (light: boolean = false) => {
    const { classes, listing, token, dispatch, user } = this.props;
    const handleBlock = () => {
      const deleteCallback = () =>
        confirmAlert({
          customUI: ({ onClose }) => {
            const handleNeverShowClick = () => {
              dispatch(blockListing(listing.id, token));
              dispatch(
                updateProfile(
                  {
                    onboarding: {
                      ...user.onboarding,
                      hideDeleteListingPromptWeb: true,
                    },
                  },
                  token,
                  false,
                  true,
                ),
              );
              onClose();
            };
            return (
              <div className={classes.alertContainerBlock}>
                <h1 className={classes.alertTitleBlock}>Remove listing</h1>
                <Typography className={classes.alertSubTextBlock}>
                  Are you sure? Once you remove this listing, you won’t see it
                  again.
                </Typography>
                <Typography className={classes.alertSubTextBlock}>
                  You can always view your removed listings under{' '}
                  <strong>Deleted Properties in your Favorites.</strong>
                </Typography>
                <div className={classes.alertButtonsBlock}>
                  <Button
                    className={classes.alertSubmitButtonBlock}
                    onClick={() => {
                      dispatch(blockListing(listing.id, token));
                      onClose();
                    }}
                    style={{ marginRight: '16px' }}
                  >
                    Okay
                  </Button>
                  <Button
                    className={classes.alertCancelButtonBlock}
                    onClick={handleNeverShowClick}
                  >
                    Never show again
                  </Button>
                </div>
              </div>
            );
          },
        });
      if (!token) {
        dispatch({
          signupSubtext: `Sign up to save your favorite homes or block the ones you don’t want to see again.`,
          type: OpenSignupDialog,
          signupCallback: () => {
            deleteCallback(); // Callback function to execute after auth
            // Clear the callback from reducer
            dispatch({
              type: ClearSignupCallback,
            });
          },
        });
      } else {
        // Check if the user has opted to hide the delete prompt forever
        if (get(user, 'onboarding.hideDeleteListingPromptWeb', false)) {
          dispatch(blockListing(listing.id, token));
        } else {
          deleteCallback();
        }
      }
    };
    const shareListingURL = () => {
      if (isMobile() || isTablet()) {
        if (this.supportsNativeShare) {
          navigator
            .share({
              text: 'Check out this home I found on Torii!',
              title: 'Torii Property',
              url: `${window.location.origin}/listing/r/${
                listing.id
              }/${formatUrlAddress(listing)}`,
            })
            .then(() => null)
            .catch(error => null);
        }
      } else {
        if (this.supportsCopy) {
          const uri = encodeURI(
            `${window.location.origin}/listing/r/${
              listing.id
            }/${formatUrlAddress(listing)}`,
          );
          navigator.clipboard.writeText(uri);
        }
      }
    };
    return (
      <div
        className={classes.iconContainer}
        style={{
          alignItems: isMobile() ? 'center' : 'flex-end',
          width: token ? (isMobile() ? 85 : 110) : isMobile() ? 80 : 25,
        }}
      >
        <ButtonWithSnackBar
          hideSnack={isMobile() || isTablet() ? true : false}
          onSubmit={shareListingURL}
          buttonIcon={<Share light={light} />}
          buttonSize="small"
          buttonStyle={{
            margin: '0 7.5px',
            minWidth: 19,
            height: 19,
            padding: 0,
          }}
          buttonLabel="Share this listing"
          variant="success"
          snackBarMessage="Copied link to clipboard"
        />
        <Button
          aria-label="Block this listing"
          style={{ margin: '0 7.5px', minWidth: 19, padding: 0 }}
          onClick={event => {
            event.stopPropagation();
            handleBlock();
          }}
          size="medium"
        >
          <TrashIcon light={light} small />
        </Button>
        <FavoriteButton
          containerStyle={{
            margin: '0 7.5px',
            position: 'relative',
            padding: 0,
            top: 0,
            right: 0,
            zIndex: 0,
          }}
          listingId={listing.id}
          light={light}
          small
        />
      </div>
    );
  };

  _renderButtons = () => {
    const { classes, listing } = this.props;
    const listingSold = checkStatusSold(listing);
    return (
      <div className={classes.buttonContainer}>
        <Button
          className={
            listingSold ? classes.disabledButtonStyle : classes.buttonStyle
          }
          onClick={() => {
            this.handleRequest(listing);
          }}
          disabled={listingSold}
        >
          <Typography
            className={
              listingSold ? classes.disabledButtonText : classes.buttonText
            }
          >
            Request Info
          </Typography>
        </Button>
        <Button
          className={
            listingSold ? classes.disabledButtonStyle : classes.buttonStyle
          }
          onClick={this.handleTour}
          disabled={listingSold}
        >
          <Typography
            className={
              listingSold ? classes.disabledButtonText : classes.buttonText
            }
          >
            Tour
          </Typography>
        </Button>
        <Button
          className={
            listingSold ? classes.disabledButtonStyle : classes.buttonStyle
          }
          onClick={this.handleOfferClick}
          disabled={listingSold}
        >
          <Typography
            className={
              listingSold ? classes.disabledButtonText : classes.buttonText
            }
          >
            Start Offer
          </Typography>
        </Button>
      </div>
    );
  };

  _renderHeader = (listing: any) => {
    const { classes } = this.props;
    return (
      <div ref={this.headerRef} className={classes.headerContainer}>
        <div className="row" style={{ flexWrap: 'inherit' }}>
          {!this.props.showInModal && <BackButton />}
          <div className={classes.column1}>
            <div className={classes.column1Inner}>
              {this._renderPrice(listing)}
              <span className={classes.detailsPadder}>
                {this._renderDeatils(listing)}
              </span>
            </div>
            {this._renderAddress(listing)}
          </div>
        </div>
        {this._renderButtons()}
        <div className={classes.iconButtonsContainer}>
          {this._renderIcons()}
          {this._renderStatus(listing)}
        </div>
        {this.props.showInModal && this._renderModalOuterCloseButton()}
      </div>
    );
  };

  _renderModalOuterCloseButton = () => {
    const { classes } = this.props;
    return (
      <div style={{ position: 'relative' }}>
        <IconButton
          className={classes.closeModalButtonOuter}
          onClick={() => this.props.dispatch(toggleListingModal(false))}
          style={{ position: 'fixed' }}
          disableTouchRipple
          disableFocusRipple
        >
          <X size={32} weight="regular" className={classes.closeButtonOuter} />
        </IconButton>
      </div>
    );
  };

  _renderModalCloseButton = () => {
    const { classes } = this.props;
    return (
      <IconButton
        className={classes.closeModalButton}
        onClick={() => this.props.dispatch(toggleListingModal(false))}
      >
        <X size={32} weight="regular" />
      </IconButton>
    );
  };

  handleTabChange = (newIndex: number) => {
    // Scroll hidden tab buttons when selecting previous tab
    if (isMobile()) {
      if (newIndex > this.state.currentIndex) {
        const indexToMove =
          newIndex + 2 > this.state.routes.length - 1
            ? newIndex + 1
            : newIndex + 2;
        const currentTab = this._tabItems[indexToMove];
        if (currentTab) {
          currentTab.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      } else {
        const indexToMove = newIndex - 2 < 0 ? newIndex - 1 : newIndex - 2;
        const currentTab = this._tabItems[indexToMove];
        if (currentTab) {
          currentTab.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }
    }
    if (this.state.currentIndex !== newIndex) {
      this.setState({
        currentIndex: newIndex,
      });
    }
  };

  _renderTabs = () => {
    const { classes, listing } = this.props;
    const { currentIndex, routes } = this.state;
    const listingSold = checkStatusSold(listing);
    return (
      <div ref={this.tabBarRef} className={classes.tabContainer}>
        <div className={classes.tabInner}>
          {routes.map((route: any, index: number) => {
            const selected = currentIndex === index;
            const disableTour = route.title === 'Tour' && listingSold;
            return (
              <Button
                id={route.key}
                ref={ref => {
                  if (ref) {
                    this._tabItems[index] = ref;
                  }
                }}
                key={route.title}
                className={
                  disableTour
                    ? classes.tabButtonDisabled
                    : selected
                    ? classes.tabButtonSelected
                    : classes.tabButton
                }
                disabled={disableTour}
                onClick={() => this.handleTabChange(index)}
              >
                <Typography
                  className={classes.tabButtonText}
                  style={{ color: selected ? 'white' : '#8B8E93' }}
                >
                  {route.title}
                </Typography>
              </Button>
            );
          })}
        </div>
      </div>
    );
  };

  renderTabContents = (carouselContentHeight: number) => {
    const { classes, favorites, listing, router } = this.props;
    const {
      currentImageIndex,
      routes,
      show3DView,
      showImage,
      showMap,
      showStreetView,
    } = this.state;
    return routes.map((route: any) => {
      if (route.key === 'photos') {
        return (
          <div key={route.title}>
            <Photos
              contentHeight={carouselContentHeight}
              currentImageIndex={currentImageIndex}
              favorites={favorites}
              handle3DView={this.handle3DView}
              handleHideMap={this.handleHideMap}
              handleShowMap={this.handleShowMap}
              handleStreetView={this.handleStreetView}
              handleViewerClose={this.handleViewerClose}
              handleViewerOpen={this.handleViewerOpen}
              listing={listing}
              onImageChange={this.onImageChange}
              renderIcons={this._renderIcons}
              router={router}
              show3DView={show3DView}
              showMap={showMap}
              showStreetView={showStreetView}
              viewerIsOpen={showImage}
              listingModalOpen={this.props.showInModal}
            />
            {isMobile() && isIOS && <div className={classes.dummyContainer} />}
          </div>
        );
      } else if (route.key === 'details') {
        return (
          <div
            key={route.title}
            className={classes.carouselContentContainer}
            style={{ height: carouselContentHeight }}
          >
            <Details listing={listing} showInModal={this.props.showInModal} />
            {isMobile() && isIOS && <div className={classes.dummyContainer} />}
          </div>
        );
      } else if (route.key === 'tour') {
        return (
          <div
            key={route.title}
            className={`${classes.carouselContentContainer} ${
              !this.props.showInModal ? classes.carouselExtraClass : ''
            }`}
            style={{ height: carouselContentHeight }}
          >
            <Tour
              listing={listing}
              containerHeight={
                this.props.showInModal
                  ? carouselContentHeight
                  : carouselContentHeight - 40
              }
            />
            {isMobile() && isIOS && <div className={classes.dummyContainer} />}
          </div>
        );
      } else if (route.key === 'insights') {
        return (
          <div
            key={route.title}
            className={classes.carouselContentContainer}
            style={{ height: carouselContentHeight }}
          >
            <realreports-widget
              token="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1aWQiOjExOTMxLCJhcGkiOnRydWUsImlhdCI6MTczODMzNDgxNywiZXhwIjoxODAxNDA2ODE3fQ.iwOO21y0yroC9Uecf1xkVmLiRRQ2DWC58ViYAJ_Z_UI"
              address={listing.address}
              latitude={listing.latitude}
              longitude={listing.longitude}
              leads-email="info@toriihomes.com"
              theme="#443461"
              fields="overallClimateRisk,estElectricityCost,maxAvailableInternetSpeed,populationZipCode,medianHouseholdIncome,percentOwnerOccupied,percentRenterOccupied,floodZone,floodInsurance,countyLowAirQualityDaysPerYear,percentPeopleAttainedBachelorDegreeOrHigher,employmentRate,avgWorkCommuteTime,countyRadonLevels,walkScore,bikeScore,noiseLevels,transitScore"
              mls-listing-id={listing.mlsListingId}
              mls-ouid="M00000306"
            ></realreports-widget>
          </div>
        );
      } else if (route.key === 'neighborhood') {
        return (
          <div
            key={route.title}
            className={classes.carouselContentContainer}
            style={{
              height: carouselContentHeight,
              width: '100%',
              paddingLeft: '30px',
            }}
          >
            <Neighborhood
              listing={listing}
              showInModal={this.props.showInModal}
            />
            {isMobile() && isIOS && <div className={classes.dummyContainer} />}
          </div>
        );
      } else if (route.key === 'schools') {
        return (
          <div
            key={route.title}
            className={classes.carouselContentContainer}
            style={{ height: carouselContentHeight }}
          >
            <Schools openInModal={this.props.showInModal} />
            {isMobile() && isIOS && <div className={classes.dummyContainer} />}
          </div>
        );
      } else if (route.key === 'estimate') {
        return (
          <div
            key={route.title}
            className={classes.carouselContentContainer}
            style={{ height: carouselContentHeight }}
          >
            <Estimate
              listing={listing}
              showEditButton={this.handleEstimateEditButton}
            />
            {isMobile() && isIOS && <div className={classes.dummyContainer} />}
          </div>
        );
      } else {
        return (
          <div
            key={route.title}
            className={classes.carouselContentContainer}
            style={{ height: carouselContentHeight }}
          >
            <Similar />
            {isMobile() && isIOS && <div className={classes.dummyContainer} />}
          </div>
        );
      }
    });
  };

  _onMapButtonClick = () => {
    this.setState({
      showMap: true,
      showMapButton: false,
    });
  };

  _on3DViewButtonClick = () => {
    const { virtualTourUrl } = this.props.listing;
    if (virtualTourUrl.includes('matterport')) {
      this.setState({
        show3DView: true,
        show3DViewButton: false,
      });
    } else {
      const win: any = window.open(virtualTourUrl, '_blank');
      win.focus();
    }
  };
  _onStreeViewButtonClick = () => {
    this.setState({
      showStreetView: true,
      showStreetViewButton: false,
    });
  };

  handleEstimateEditButton = (showEditButton: boolean) => {
    this.setState({
      showEditButton,
    });
  };

  handleEstimateForm = () => {
    EventEmitter.emit('showCompsForm');
  };

  handleShowMap = () => {
    this.setState({
      showMap: true,
    });
  };

  handle3DView = (show3DView: boolean) => {
    this.setState({
      show3DView,
      show3DViewButton: show3DView ? false : true,
    });
  };

  handleStreetView = (showStreetView: boolean) => {
    this.setState({
      showStreetView,
      showStreetViewButton: showStreetView ? false : true,
    });
  };

  handleHideMap = () => {
    this.setState({
      showMap: false,
      showMapButton: true,
    });
  };

  animateCard = () => {
    this.setState({ drawerOpen: !this.state.drawerOpen });
  };

  onImageChange = (currentImageIndex: number) => {
    this.setState({ currentImageIndex });
  };

  handleViewerOpen = (currentImageIndex: number) => {
    this.setState({
      currentImageIndex,
      showImage: true,
    });
  };
  handleViewerClose = () => {
    this.setState({
      currentImageIndex: 0,
      showImage: false,
    });
  };

  _renderTabCarousel = (
    carouselContentHeight: number,
    currentIndex: number,
  ) => {
    return (
      <Carousel
        autoplay={false}
        cellAlign="center"
        cellSpacing={50}
        className={'listing-carousel'}
        dragging={false}
        heightMode="current"
        pauseOnHover={false}
        slideIndex={currentIndex}
        slidesToShow={1}
        style={{ height: 'auto' }}
        swiping={false}
        withoutControls
      >
        {this.renderTabContents(carouselContentHeight)}
      </Carousel>
    );
  };

  render() {
    const { classes, listing, showInModal } = this.props;
    const {
      currentIndex,
      drawerOpen,
      height,
      loading,
      show3DViewButton,
      showEditButton,
      showImage,
      streetViewSupported,
      showStreetView,
      show3DView,
      showMap,
      width,
    } = this.state;
    const modalPadding = showInModal ? 70 : 0; // Modal Top padding height
    const carouselContentHeight = isMobile()
      ? height - TABS_HEADER_HEIGHT - HEADER_HEIGHT - modalPadding
      : isTablet()
      ? height - 160 - TABS_HEADER_HEIGHT - HEADER_HEIGHT - modalPadding
      : height -
        DETAILS_HEADER_HEIGHT -
        TABS_HEADER_HEIGHT -
        HEADER_HEIGHT -
        modalPadding;
    const showMapButtons =
      currentIndex === 0 && !showMap && !show3DView && !showStreetView;
    return (
      <div
        id="listing-box-container"
        className={`${classes.basicContainer} listing-container`}
        style={
          showInModal
            ? { height: 'auto', maxHeight: 'auto' }
            : {
                height: this.state.height - HEADER_HEIGHT,
                maxHeight: this.state.height - HEADER_HEIGHT,
              }
        }
      >
        {!isMobile() && this._renderHeader(listing)}
        {this._renderTabs()}
        <div
          className={classes.pattern}
          style={{ height: carouselContentHeight }}
        />
        {loading ? (
          <ToriiLoader />
        ) : (
          <React.Fragment key={`${showImage}`}>
            {this._renderTabCarousel(carouselContentHeight, currentIndex)}
            {isMobile() && (
              <React.Fragment>
                <div className={classes.sideButtonsContainer}>
                  <AnimateSharedLayout>
                    <motion.div
                      layout
                      initial={false}
                      animate={{
                        opacity: showMapButtons && show3DViewButton ? 1 : 0,
                        display:
                          showMapButtons && show3DViewButton
                            ? 'initial'
                            : 'none',
                      }}
                    >
                      <Button
                        onClick={this._on3DViewButtonClick}
                        classes={{
                          label: classes.mapButtonLabel,
                          root: classes.mapButton,
                        }}
                      >
                        <ThreeDimensionIcon />
                        <Typography
                          style={{ fontSize: 12, lineHeight: '15px' }}
                        >
                          3D
                        </Typography>
                      </Button>
                    </motion.div>
                    <AnimatePresence>
                      {showMapButtons && streetViewSupported && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                        >
                          <Button
                            onClick={this._onStreeViewButtonClick}
                            classes={{
                              label: classes.mapButtonLabel,
                              root: classes.mapButton,
                            }}
                          >
                            <StreetViewIcon />
                            <Typography
                              style={{ fontSize: 12, lineHeight: '15px' }}
                            >
                              Street
                            </Typography>
                          </Button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </AnimateSharedLayout>
                  {showMapButtons && (
                    <Button
                      onClick={this._onMapButtonClick}
                      classes={{
                        label: classes.mapButtonLabel,
                        root: classes.mapButton,
                      }}
                    >
                      <MapIcon fillColor="#463264" />
                      <Typography style={{ fontSize: 12, lineHeight: '15px' }}>
                        Map
                      </Typography>
                    </Button>
                  )}
                  {currentIndex === 5 && showEditButton && (
                    <Button
                      onClick={this.handleEstimateForm}
                      classes={{
                        label: classes.settingsButtonLabel,
                        root: classes.mapButton,
                      }}
                    >
                      <SettingsIcon fillColor="#463264" />
                    </Button>
                  )}
                </div>
                <div style={{ position: 'fixed', bottom: 0 }}>
                  <AnimateHeight
                    height={drawerOpen ? 160 : 55}
                    style={{
                      ...styles.sliderContainer,
                      bottom: 0,
                      width,
                    }}
                    onClick={this.animateCard}
                  >
                    <div
                      className={classes.sliderInnerContainer}
                      style={{ width, bottom: -TABS_HEADER_HEIGHT }}
                    >
                      <div className={classes.sliderButton} />
                      <div className={classes.sliderFirstRow}>
                        {this._renderPrice(listing)}
                        {this._renderDeatils(listing)}
                        {this._renderIcons()}
                      </div>
                      <div className={classes.sliderSecondRow}>
                        {this._renderAddress(listing)}
                        {this._renderStatus(listing)}
                      </div>
                      <div className={classes.sliderThirdRow}>
                        {this._renderButtons()}
                      </div>
                    </div>
                  </AnimateHeight>
                </div>
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  favorites: state.favorites.favorites,
  listing: state.listings.listing,
  token: state.auth.token,
  user: state.auth.user,
});

export default connect(mapStateToProps)(
  withStyles(styles)(withRouter(Listing)),
);
